/**
 * Navigation Paths Configuration
 * Defines all application routes with their paths, authentication, and role requirements
 */

import { applyCollectionEntityToPath } from '#app/utils/pz/brand/collectionEntity'
import { applyMainEntityToPath } from '#app/utils/pz/brand/mainEntity'

export const navigationPaths = {
	home: { path: '/', enabled: true, requiresAuth: false },
	pricing: { path: '/pricing', enabled: true, requiresAuth: false },
	checkout: { path: '/checkout', enabled: true, requiresAuth: true },
	legal: { path: '/legal', enabled: true, requiresAuth: false },
	blog: { path: '/blog', enabled: false, requiresAuth: false },
	about: { path: '/about', enabled: false, requiresAuth: false },
	contact: { path: '/contact', enabled: true, requiresAuth: false },
	dashboard: { path: '/learn', enabled: true, requiresAuth: true },
	studyPanel: {
		base: { path: '/learn/dashboard', enabled: true, requiresAuth: true },
	},
	docs: {
		base: {
			path: '/docs',
			enabled: true,
			requiresAuth: false,
		},
	},
	internalDocs: {
		base: {
			path: '/internal-docs',
			enabled: true,
			requiresAuth: true,
			allowedRoles: ['admin', 'editor'] as const,
		},
	},
	auth: {
		login: { path: '/login', enabled: true, requiresAuth: false },
		signup: { path: '/signup', enabled: true, requiresAuth: false },
		forgotPassword: {
			path: '/forgot-password',
			enabled: true,
			requiresAuth: false,
		},
		verify: { path: '/verify', enabled: true, requiresAuth: false },
		onboarding: { path: '/onboarding', enabled: true, requiresAuth: false },
		resetPassword: {
			path: '/reset-password',
			enabled: true,
			requiresAuth: false,
		},
		logout: { path: '/logout', enabled: true, requiresAuth: true },
	},
	collections: {
		base: {
			path: applyCollectionEntityToPath('/learn/collections'),
			enabled: true,
			requiresAuth: true,
		},
		detail: {
			path: applyCollectionEntityToPath('/learn/collections/:collectionSlug'),
			enabled: true,
			requiresAuth: true,
		},
	},
	courses: {
		base: {
			path: applyMainEntityToPath('/learn/courses'),
			enabled: true,
			requiresAuth: true,
		},
		detail: {
			path: applyMainEntityToPath('/learn/courses/:courseId'),
			enabled: true,
			requiresAuth: true,
		},
	},
	marketing: {
		collectionDetail: {
			path: applyCollectionEntityToPath('/collections/:collectionSlug'),
			enabled: true,
			requiresAuth: false,
		},
		courses: {
			path: applyMainEntityToPath('/courses'),
			enabled: true,
			requiresAuth: false,
		},
		courseDetail: {
			path: applyMainEntityToPath('/courses/:courseSlug'),
			enabled: true,
			requiresAuth: false,
		},
		coursePreview: {
			path: applyMainEntityToPath('/courses/:courseSlug/preview'),
			enabled: true,
			requiresAuth: false,
		},
	},
	publicCourses: {
		base: {
			path: applyMainEntityToPath('/courses'),
			enabled: true,
			requiresAuth: false,
		},
		detail: {
			path: applyMainEntityToPath('/courses/:courseSlug'),
			enabled: true,
			requiresAuth: false,
		},
		preview: {
			path: applyMainEntityToPath('/courses/:courseSlug/preview'),
			enabled: true,
			requiresAuth: false,
		},
		info: {
			path: applyMainEntityToPath('/courses/:courseSlug/info'),
			enabled: true,
			requiresAuth: false,
		},
	},
	resources: {
		themeSwitch: {
			path: '/resources/theme-switch',
			enabled: true,
			requiresAuth: true,
			allowedRoles: ['admin'] as const,
		},
		downloadUserData: {
			path: '/resources/download-user-data',
			enabled: true,
			requiresAuth: true,
		},
	},
	topics: {
		base: { path: '/learn/topics', enabled: true, requiresAuth: true },
		detail: {
			path: applyMainEntityToPath('/learn/courses/:courseId/topics/:topicId'),
			enabled: true,
			requiresAuth: true,
		},
		resources: {
			base: {
				path: applyMainEntityToPath('/learn/courses/:courseId/resources'),
				enabled: true,
				requiresAuth: true,
			},
			detail: {
				path: applyMainEntityToPath(
					'/learn/courses/:courseId/resources/:resourceId',
				),
				enabled: true,
				requiresAuth: true,
			},
		},
	},
	settings: {
		base: { path: '/settings', enabled: true, requiresAuth: true },
		account: { path: '/settings/account', enabled: true, requiresAuth: true },
		appearance: {
			path: '/settings/appearance',
			enabled: true,
			requiresAuth: true,
			allowedRoles: ['admin'] as const,
		},
		billing: { path: '/settings/billing', enabled: true, requiresAuth: true },
		profile: {
			path: '/settings/profile-basic',
			enabled: true,
			requiresAuth: true,
		},
		notifications: {
			path: '/settings/notifications',
			enabled: true,
			requiresAuth: true,
		},
		display: { path: '/settings/display', enabled: true, requiresAuth: true },
	},
	admin: {
		users: {
			base: {
				path: '/admin/users',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
			view: {
				path: '/admin/users/:userId',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
			edit: {
				path: '/admin/users/:userId/edit',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
			delete: {
				path: '/admin/users/:userId/delete',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
			impersonate: {
				path: '/admin/users/:userId/impersonate',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
		},
		roles: {
			base: {
				path: '/admin/roles',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
			view: {
				path: '/admin/roles/:roleId',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
			edit: {
				path: '/admin/roles/:roleId/edit',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
			delete: {
				path: '/admin/roles/:roleId/delete',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
		},
		courses: {
			base: {
				path: applyMainEntityToPath('/admin/courses'),
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin', 'editor'] as const,
			},
			view: {
				path: applyMainEntityToPath('/admin/courses/:courseId'),
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin', 'editor'] as const,
			},
			edit: {
				path: applyMainEntityToPath('/admin/courses/:courseId/edit'),
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin', 'editor'] as const,
			},
			delete: {
				path: applyMainEntityToPath('/admin/courses/:courseId/delete'),
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin', 'editor'] as const,
			},
			creation: {
				base: {
					path: applyMainEntityToPath('/admin/courses/creation'),
					enabled: true,
					requiresAuth: true,
					allowedRoles: ['admin'] as const,
				},
				list: {
					path: applyMainEntityToPath('/admin/recent-creations'),
					enabled: true,
					requiresAuth: true,
					allowedRoles: ['admin'] as const,
				},
			},
		},
		categories: {
			base: {
				path: '/admin/categories',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin', 'editor'] as const,
			},
		},
		configStudio: {
			base: {
				path: '/admin/configStudio',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
		},
		emailStudio: {
			base: {
				path: '/admin/emailStudio',
				enabled: true,
				requiresAuth: true,
				allowedRoles: ['admin'] as const,
			},
		},
	},
} as const

export type NavigationPaths = typeof navigationPaths
